/**
 * HR help text for the Policy Management module.
 *
 * Attendance policies do not change anything on their own. They are attached to
 * employees through a Policy Planner and are calculated while payroll is being
 * processed, using the unprocessed attendance of the payroll's attendance
 * cut-off period. Every deduction or earning they produce lands on the payslip.
 */

// Wording shared by every attendance policy, so the nine policy screens stay consistent.
const policyCommon = (subject, produces) => ({
  name: `Policy name HR will see while attaching this ${subject} rule to a policy planner. Use a name your team recognizes, such as Head Office ${subject}.`,
  description: `Internal note explaining when this policy should be used and what ${produces} it creates. Only HR sees it.`,
  slabs: `Slabs decide the outcome. Each slab sets a range, and when an employee's ${subject} falls inside that range, the slab's amount rule is applied during payroll.`,
});

const lateArrivalPolicy = {
  ...policyCommon("late arrival", "salary deduction"),
  is_daywise: "Daily checks each late day on its own and applies a slab per day. Monthly adds up all late minutes of the month first and applies a slab once to that total. Daily usually deducts more.",
  ignore_buffer: "Turn this on to count late minutes from the exact shift start time, ignoring the grace minutes set on the shift. Employees lose the grace period for this policy.",
};

const earlyLeftPolicy = {
  ...policyCommon("early leaving", "salary deduction"),
  is_daywise: "Daily checks each early-leaving day on its own and applies a slab per day. Monthly adds up all early minutes of the month first and applies a slab once to that total.",
  ignore_buffer: "Turn this on to count early minutes from the exact shift end time, ignoring the grace minutes set on the shift.",
};

const relaxationPolicy = {
  ...policyCommon("relaxation", "salary deduction"),
  is_daywise: "Daily applies a slab to each relaxation day on its own. Monthly adds up the month's relaxation minutes first and applies a slab once to the total.",
  ignore_buffer: "Turn this on to count relaxation minutes from the exact shift timing, ignoring the shift's grace minutes.",
};

const shortDurationPolicy = {
  ...policyCommon("short duration", "salary deduction"),
  is_daywise: "Daily applies a slab to each short-duration day on its own. Monthly adds up the month's shortfall minutes first and applies a slab once to the total.",
};

const absentPolicy = {
  ...policyCommon("absence", "salary deduction"),
  slabs: "Absence slabs are counted by how many times the employee was absent in the payroll month, not by minutes. Set the occurrence range and the deduction that applies at those occurrences.",
};

const missingPolicy = {
  ...policyCommon("missing attendance", "salary deduction"),
  slabs: "Missing-attendance slabs are counted by how many times attendance was missing in the payroll month, not by minutes. A missing day where the employee was on leave is not counted.",
};

const leavePolicy = {
  ...policyCommon("deductible leave", "salary deduction"),
  is_daywise: "Daily applies a slab to each deductible leave shift on its own. Monthly counts all deductible leave of the month first and applies a slab once.",
  slabs: "Only leave types marked deductible reach this policy. For short leave the deduction is scaled by the short leave slab's quota, so a half-day short leave deducts half.",
};

const overtimePolicy = {
  ...policyCommon("overtime", "salary earning"),
  is_daywise: "Daily pays each approved overtime record on its own. Monthly adds up the month's approved overtime minutes and pays against the total.",
  slabs: "Overtime slabs match the approved overtime minutes and decide how much is added to salary. Only approved, unpaid overtime records are used.",
};

const cplPolicy = {
  ...policyCommon("compensatory leave (CPL)", "leave quota earning"),
  is_daywise: "Daily converts each overtime day on its own. Monthly adds up the month's overtime minutes and converts the total once.",
  slabs: "CPL slabs match overtime minutes and decide how much compensatory leave quota the employee earns instead of money.",
};

// Attendance policy planner: what actually assigns policies to employees.
const planner = {
  name: "Name of this policy plan. HR sees it when checking which attendance rules an employee falls under.",
  description: "Internal note explaining which group of employees this plan is meant for.",
  date: "The date this plan becomes effective. During payroll the system picks the latest plan dated on or before the period end, so a future date leaves the current plan running until then.",
  for_all: "Apply this plan to every employee of the company. Turn it off to attach the plan to selected employees only.",
  employment_ids: "The employees this plan applies to. An employee not listed here, and not covered by an all-employee plan, gets no attendance deduction or earning at all.",
  absent_policy_id: "Rule used to deduct salary for absent days in this plan.",
  missing_policy_id: "Rule used to deduct salary when attendance is missing.",
  late_arrival_policy_id: "Rule used to deduct salary for late arrivals.",
  early_left_policy_id: "Rule used to deduct salary when employees leave early.",
  short_duration_policy_id: "Rule used when worked time is less than the expected shift time.",
  relaxation_policy_id: "Rule used for relaxation cases.",
  leave_policy_id: "Rule used to deduct salary for deductible leave.",
  overtime_policy_id: "Rule used to pay approved overtime in salary.",
  cpl_policy_id: "Rule used to convert overtime into compensatory leave quota instead of money.",
};

// Request policy planner: assigns the travel policy used to pay travel claims.
const requestPlanner = {
  name: "Name of this request policy plan. HR sees it when checking which travel entitlement an employee falls under.",
  description: "Internal note explaining which group of employees this plan is meant for.",
  date: "The date this plan becomes effective. Travel claims processed in payroll use the latest plan dated on or before the period.",
  for_all: "Apply this plan to every employee. Turn it off to attach it to selected employees only.",
  employment_ids: "The employees this plan applies to. Employees not covered by any plan get no travel allowance in payroll.",
  travel_policy_id: "Travel policy that decides the stay and daily allowance amounts paid for this group's travel requests.",
};

const travelPolicy = {
  name: "Travel policy name, such as Management Travel or Field Staff Travel. HR selects it inside a request policy plan.",
  description: "Internal note explaining which grade or group this entitlement is meant for.",
  details: "Each row sets the amounts for one travel type. A travel request whose travel type has no row here is paid nothing.",
  traveling_type_id: "Travel type these amounts apply to, such as Local or International. The employee's travel request must use this same type to be paid.",
  traveling_mode_id: "Travel mode allowed for this travel type, such as air, rail, or road.",
  company_stay_amount: "Amount paid per night when the company arranges the stay. Payroll multiplies it by the number of nights on the travel request.",
  self_stay_amount: "Amount paid per night when the employee arranges their own stay. Payroll multiplies it by the number of nights on the travel request.",
  daily_allowance: "Amount paid per day of travel, covering food and local expenses. Payroll multiplies it by the number of travel days, counting both the departure and the arrival day.",
};

const deductionType = {
  name: "HR-friendly reason for the deduction, such as Per Minute Salary Deduction. It is shown while building policy slabs.",
  from_salary: "Salary deducts money using the selected formula. Quota deducts from the employee's leave balance instead of money.",
  type: "Flat deducts the exact value entered. Percentage deducts that percent of the formula value. As Per Actual deducts that percent for every actual late, early, or short minute.",
  formula_id: "Salary formula used as the base amount. Percentage and As Per Actual are calculated from this, so the formula decides whether the deduction is per day, per shift, or per minute.",
  value: "The number applied by this type. For Flat it is the amount deducted. For Percentage and As Per Actual it is a percent, so 100 means the full formula value.",
  description: "Internal note explaining when this deduction type should be picked in a policy slab.",
};

const encashmentType = {
  name: "HR-friendly reason for the earning, such as Overtime Encashment. It is shown while building policy slabs.",
  from_salary: "Salary pays money using the selected formula. Quota adds leave balance instead of money.",
  type: "Flat pays the exact value entered. Percentage pays that percent of the formula value. As Per Actual pays that percent for every actual approved minute.",
  formula_id: "Salary formula used as the base amount. Percentage and As Per Actual are calculated from this.",
  value: "The number applied by this type. For Flat it is the amount paid. For Percentage and As Per Actual it is a percent, so 100 means the full formula value.",
  description: "Internal note explaining when this earning type should be picked in a policy slab.",
};

// Slab fields. The same modal is reused by every policy, so the wording is
// tuned per policy type where the same field means something different.
const slabCommon = {
  start_mins: "The slab starts applying from this many minutes. An employee below this number is not touched by this slab.",
  end_mins: "The slab stops applying after this many minutes. Leave it empty when this slab should cover everything above the start value, otherwise minutes beyond this number match no slab and nothing is deducted.",
  min_times: "The occurrence at which this slab starts applying within the payroll month, for example 3 to start from the third time.",
  max_times: "The last occurrence this slab covers. Leave it empty when there is no upper limit.",
  repeat_id: "Controls whether the slab is applied only once at the starting occurrence, or again after every selected number of occurrences.",
  deduction_type_id: "The deduction applied when this slab matches. It decides whether salary or leave quota is deducted, and how much.",
  fallback_type_id: "Used when the employee has no leave quota left. The quota deduction is applied first, and whatever cannot be covered by quota is deducted from salary using this rule.",
  encashment_type_id: "The earning applied when this slab matches. It decides whether the employee is paid money or given leave quota, and how much.",
  min: "The slab starts applying from this many approved minutes.",
  max: "The slab stops applying after this many approved minutes. Leave it empty when there is no upper limit.",
};

const slabByPolicy = {
  lateArrival: {
    start_mins: "The slab starts applying from this many late minutes. Arriving less late than this matches no slab and is not deducted.",
    end_mins: "The slab stops applying after this many late minutes. Leave it empty for the last slab so heavier late cases are still covered.",
  },
  earlyLeft: {
    start_mins: "The slab starts applying from this many early-leaving minutes.",
    end_mins: "The slab stops applying after this many early-leaving minutes. Leave it empty for the last slab.",
  },
  relaxation: {
    start_mins: "The slab starts applying from this many relaxation minutes.",
    end_mins: "The slab stops applying after this many relaxation minutes. Leave it empty for the last slab.",
  },
  shortDuration: {
    start_mins: "The slab starts applying from this many minutes short of the expected shift time.",
    end_mins: "The slab stops applying after this many short minutes. Leave it empty for the last slab.",
  },
  leave: {
    start_mins: "Keep this at 1. Deductible leave is counted per leave shift, not in minutes.",
    end_mins: "Leave this empty. Deductible leave is counted per leave shift, not in minutes.",
    min_times: "The leave occurrence at which this slab starts applying in the payroll month, for example 2 to start deducting from the second deductible leave.",
    max_times: "The last leave occurrence this slab covers. Leave it empty when there is no upper limit.",
  },
  absent: {
    min_times: "The absence at which this slab starts applying in the payroll month, for example 2 to start deducting from the second absence.",
    max_times: "The last absence this slab covers. Leave it empty when there is no upper limit.",
    repeat_id: "Choose whether the deduction happens only at the starting absence, or again after every selected number of absences.",
  },
  missing: {
    min_times: "The missing-attendance occurrence at which this slab starts applying in the payroll month.",
    max_times: "The last occurrence this slab covers. Leave it empty when there is no upper limit.",
    repeat_id: "Choose whether the deduction happens only at the starting occurrence, or again after every selected number of occurrences.",
  },
  overtime: {
    min: "The slab starts applying from this many approved overtime minutes.",
    max: "The slab stops applying after this many approved overtime minutes. Leave it empty for the last slab so longer overtime is still paid.",
    encashment_type_id: "The earning applied when this slab matches, deciding how much overtime money is added to the payslip.",
  },
  cpl: {
    min: "The slab starts applying from this many overtime minutes.",
    max: "The slab stops applying after this many overtime minutes. Leave it empty for the last slab.",
    encashment_type_id: "The earning applied when this slab matches, deciding how much compensatory leave quota the employee receives.",
  },
};

/**
 * Slab help text for one policy type, falling back to the shared wording.
 * `policyType` is the value the policy screen passes to the slab modal
 * ('lateArrival', 'overtime', ...).
 */
export const getSlabTooltips = (policyType) => ({
  ...slabCommon,
  ...(slabByPolicy[policyType] || {}),
});

// Read-only previews of the policy that a plan has attached.
const attachedPolicy = {
  name: "Name of the policy attached to this plan. To change how it calculates, open that policy from its own screen.",
  description: "The note saved on the attached policy, explaining when it should be used.",
};

export const policyTooltips = {
  attachedPolicy,
  absentPolicy,
  missingPolicy,
  lateArrivalPolicy,
  earlyLeftPolicy,
  shortDurationPolicy,
  relaxationPolicy,
  leavePolicy,
  overtimePolicy,
  cplPolicy,
  planner,
  requestPlanner,
  travelPolicy,
  deductionType,
  encashmentType,
  slab: slabCommon,
};

export default policyTooltips;
